import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaQuoteRight, FaArrowRight, FaArrowLeft, FaStar, FaPause, FaPlay } from 'react-icons/fa';
import amina from '../assets/amina.jpg';
import daniel from '../assets/daniel.jpg';
import jane from '../assets/jane.jpg';
import john from '../assets/john.jpg';
import { useApiData } from '../hooks/useApiData';
import { fetchTestimonialsSection, fetchImage, API_BASE_URL } from '../repository/api';


const Testimonials = () => {
  const [visibleElements, setVisibleElements] = useState({});
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  
  
  const progressIntervalRef = useRef(null);
  const sectionRef = useRef(null);
  const elementsRef = useRef({
    header: null,
    carousel: null,
    shapes: []
  });

  // Fetch testimonials data from API
  const { data: testimonialsData, loading, error } = useApiData(fetchTestimonialsSection);

  // Auto-rotate carousel with visible progress
  useEffect(() => {
    if (!isPaused) {
      // Reset progress to 0 when starting a new slide
      setProgress(0);
      
      // Set up progress interval to update every 50ms
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            // Move to next slide when progress reaches 100%
            setActiveTestimonial(current => (current + 1) % testimonials.length);
            return 0;
          }
          // Increment by 1% (50ms * 20 = 1 second = 20%)
          return prev + 1;
        });
      }, 50); // Update progress every 50ms
    } else {
      // Clear progress interval when paused
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPaused, activeTestimonial]);
  
  // Pause carousel on hover
  const pauseCarousel = () => {
    setIsPaused(true);
  };
  
  const resumeCarousel = () => {
    setIsPaused(false);
  };
  
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Handle scroll-based animations with Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-5% 0px',
      threshold: 0.1
    };

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        setVisibleElements(prev => ({
          ...prev,
          [entry.target.id]: entry.isIntersecting
        }));
      });
    };
    
    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    
    Object.values(elementsRef.current).forEach(ref => {
      if (Array.isArray(ref)) {
        ref.forEach(item => {
          if (item) observer.observe(item);
        });
      } else {
        if (ref) observer.observe(ref);
      }
    });

    return () => observer.disconnect();
  }, []);

  const nextTestimonial = () => {
    setIsPaused(true);
    setProgress(0); // Reset progress
    setActiveTestimonial(prev => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setIsPaused(true);
    setProgress(0); // Reset progress
    setActiveTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Default fallback data
  const defaultTestimonials = [
    {
      id: 'testimonial-1',
      quote: "The SMS campaigns through Codewave have been working well for us. Customer response jumped up significantly - around 40% better than our old system.",
      name: "Jane Muthoni",
      title: "Marketing Director",
      company: "TechSasa Ltd",
      image: amina,
      rating: 5
    },
    {
      id: 'testimonial-2',
      quote: "Setting up the USSD service was straightforward, and it's been working great for our customers. The support team was really helpful when we had questions.",
      name: "Daniel Ochieng",
      title: "Operations Manager",
      company: "PayFast Kenya",
      image: daniel,
      rating: 5
    },
    {
      id: 'testimonial-3',
      quote: "The Codewave system really helped streamline our call operations. We're seeing around a 35% improvement in how efficiently we handle customer calls.",
      name: "Rebecca Mwendwa",
      title: "Customer Service Head",
      company: "RetailPlus Group",
      image: jane,
      rating: 5
    },
    {
      id: 'testimonial-4',
      quote: "Codewave's platform plays nicely with our other systems, which wasn't the case with what we had before. It's taken a lot of complexity out of how we handle payments.",
      name: "John Kamau",
      title: "Finance Manager",
      company: "Savannah Retailers",
      image: john,
      rating: 4
    }
  ];

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) console.log('API Data received:', apiData);
    
    if (!apiData || !apiData.testimonials || !Array.isArray(apiData.testimonials) || apiData.testimonials.length === 0) {
      if (isDebug) console.log('No valid testimonials data found, using default data');
      return defaultTestimonials;
    }
    
    const fallbackImages = [amina, daniel, jane, john];

    const transformedData = apiData.testimonials.map((testimonial, index) => ({
      id: `testimonial-${index + 1}`,
      quote: testimonial.message || 'No message available',
      name: testimonial.client_name || 'Anonymous',
      title: testimonial.role ? testimonial.role.split(',')[0] || 'Client' : 'Client',
      company: testimonial.role ? (testimonial.role.split(',')[1]?.trim() || 'Company') : 'Company',
      image: testimonial.image_path ? fetchImage(testimonial.image_path) : fallbackImages[index % fallbackImages.length],
      rating: testimonial.rating || 5
    }));
    
    if (isDebug) console.log('Transformed testimonials data:', transformedData);
    return transformedData;
  };

  // Create mock API data based on the structure you provided
  const createMockApiData = () => {
    return {
      "created_at": "2025-01-10T09:52:09",
      "id": "mock-testimonials-id",
      "testimonials": [
        {
          "client_name": "Maina",
          "image_path": "5732764a-fa2d-4393-a17a-c9477cff0fa8_03144211-c45f-46c7-a325-103c2655a05f.jpeg",
          "message": "This is the best thing i have done",
          "rating": 5,
          "role": "CEO"
        },
        {
          "client_name": "Sarah Kimani",
          "image_path": null,
          "message": "Codewave's bulk SMS service has transformed our patient communication. We've seen a 40% increase in appointment attendance since implementing their reminder system.",
          "rating": 5,
          "role": "Marketing Director, HealthPlus Clinics"
        },
        {
          "client_name": "James Omondi",
          "image_path": null,
          "message": "The analytics and reporting features have been invaluable for tracking our campaign performance. We can now make data-driven decisions about our messaging strategy.",
          "rating": 5,
          "role": "Customer Success Manager, RetailPro Kenya"
        }
      ],
      "updated_at": "2025-01-10T09:52:09"
    };
  };

  // Use API data or fallback to default - always ensure we have testimonials
  const testimonials = React.useMemo(() => {
    if (testimonialsData && !loading && !error) {
      const transformed = transformApiData(testimonialsData);
      return transformed.length > 0 ? transformed : defaultTestimonials;
    }
    
    // If API failed but we have error, try using mock data
    if (error && !loading) {
      const isDebug = process.env.NODE_ENV === 'development';
      if (isDebug) console.log('API failed, trying mock data...');
      const mockData = createMockApiData();
      const transformed = transformApiData(mockData);
      if (transformed.length > 0) {
        if (isDebug) console.log('Using mock data as fallback');
        return transformed;
      }
    }
    
    return defaultTestimonials;
  }, [testimonialsData, loading, error]);

  // Debug logging (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Testimonials component state:', {
        loading,
        error,
        hasApiData: !!testimonialsData,
        testimonialsCount: testimonials.length,
        apiData: testimonialsData
      });
    }
  }, [loading, error, testimonialsData, testimonials.length]);

  // Test API endpoint directly (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const testApi = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/testimonials`);
          console.log('Direct API test response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('Direct API test data:', data);
          } else {
            console.log('Direct API test failed with status:', response.status);
          }
        } catch (err) {
          console.log('Direct API test error:', err);
          console.log('This suggests the backend server might not be running on localhost:5003');
        }
      };
      testApi();
    }
  }, []);

  // Background shape data
  const shapes = [
    { className: "circle", color: "#105090", top: "15%", right: "8%", size: "120px", id: "testimonials-shape-1" },
    { className: "triangle", color: "#2090B0", bottom: "30%", left: "10%", size: "100px", id: "testimonials-shape-2" },
    { className: "square", color: "#105090", top: "70%", right: "15%", size: "80px", id: "testimonials-shape-3" },
    { className: "hexagon", color: "#2090B0", top: "25%", left: "15%", size: "90px", id: "testimonials-shape-4" }
  ];

  // Show loading state while fetching data (but still show default testimonials)
  if (loading) {
    return (
      <TestimonialsSection id="testimonials" ref={sectionRef}>
        <BackgroundShapes>
          {shapes.map((shape, index) => (
            <GeometricShape 
              key={shape.id}
              id={shape.id}
              ref={el => elementsRef.current.shapes[index] = el}
              className={shape.className}
              color={shape.color}
              top={shape.top}
              left={shape.left}
              right={shape.right}
              bottom={shape.bottom}
              size={shape.size}
              isVisible={visibleElements[shape.id]}
            />
          ))}
        </BackgroundShapes>

        <Container>
          <SectionHeader 
            id="testimonials-header"
            ref={el => elementsRef.current.header = el}
            isVisible={visibleElements["testimonials-header"]}
          >
            <SectionTitle>Client <HighlightText>Testimonials</HighlightText></SectionTitle>
            <SectionDescription>
              Loading testimonials from our clients...
            </SectionDescription>
          </SectionHeader>
          
          <TestimonialsContainer 
            id="testimonials-carousel"
            ref={el => elementsRef.current.carousel = el}
            isVisible={visibleElements["testimonials-carousel"]}
          >
            <TestimonialCarousel 
              onMouseEnter={pauseCarousel}
              onMouseLeave={resumeCarousel}
            >
              {defaultTestimonials.map((testimonial, index) => (
                <TestimonialCard 
                  key={testimonial.id}
                  isActive={index === activeTestimonial}
                  isNext={(index === (activeTestimonial + 1) % defaultTestimonials.length)}
                  isPrev={(index === (activeTestimonial - 1 + defaultTestimonials.length) % defaultTestimonials.length)}
                >
                  <QuoteIcon>
                    <FaQuoteRight />
                  </QuoteIcon>
                  
                  <TestimonialQuote>
                    "{testimonial.quote}"
                  </TestimonialQuote>
                  
                  <TestimonialRating>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i}><FaStar /></Star>
                    ))}
                  </TestimonialRating>
                  
                  <TestimonialAuthor>
                    <AuthorImage src={testimonial.image} alt={testimonial.name} />
                    <AuthorDetails>
                      <AuthorName>{testimonial.name}</AuthorName>
                      <AuthorTitle>{testimonial.title}, {testimonial.company}</AuthorTitle>
                    </AuthorDetails>
                  </TestimonialAuthor>
                </TestimonialCard>
              ))}
              
              <ProgressDisplay>
                <CurrentSlide>Slide {activeTestimonial + 1} of {defaultTestimonials.length}</CurrentSlide>
                <ProgressTrack>
                  <ProgressFill 
                    width={progress} 
                    isPaused={isPaused}
                  />
                </ProgressTrack>
              </ProgressDisplay>
              
              <CarouselControls>
                <ControlsRow>
                  <ControlButton onClick={prevTestimonial} aria-label="Previous testimonial">
                    <FaArrowLeft />
                  </ControlButton>
                  
                  <CarouselDots>
                    {defaultTestimonials.map((_, index) => (
                      <CarouselDot 
                        key={index}
                        isActive={index === activeTestimonial}
                        onClick={() => {
                          setIsPaused(true);
                          setActiveTestimonial(index);
                          setProgress(0);
                        }}
                      >
                        <DotLabel>Slide {index + 1}</DotLabel>
                      </CarouselDot>
                    ))}
                  </CarouselDots>
                  
                  <ControlButton onClick={nextTestimonial} aria-label="Next testimonial">
                    <FaArrowRight />
                  </ControlButton>
                  
                  <PauseButton 
                    onClick={togglePause} 
                    aria-label={isPaused ? "Play" : "Pause"}
                    isPaused={isPaused}
                  >
                    {isPaused ? <FaPlay /> : <FaPause />}
                    <ButtonTooltip>{isPaused ? "Resume autoplay" : "Pause autoplay"}</ButtonTooltip>
                  </PauseButton>
                </ControlsRow>
              </CarouselControls>
            </TestimonialCarousel>
          </TestimonialsContainer>
        </Container>
      </TestimonialsSection>
    );
  }

  // Show error state if API call failed
  if (error) {
    const isDebug = process.env.NODE_ENV === 'development';
    if (isDebug) {
      console.error('Testimonials section error:', error);
      console.log('Using default testimonials due to API error');
    }
    // Continue with default data instead of showing error
  }


  return (
    <TestimonialsSection id="testimonials" ref={sectionRef}>
      <BackgroundShapes>
        {shapes.map((shape, index) => (
          <GeometricShape 
            key={shape.id}
            id={shape.id}
            ref={el => elementsRef.current.shapes[index] = el}
            className={shape.className}
            color={shape.color}
            top={shape.top}
            left={shape.left}
            right={shape.right}
            bottom={shape.bottom}
            size={shape.size}
            isVisible={visibleElements[shape.id]}
          />
        ))}
      </BackgroundShapes>

      <Container>
        <SectionHeader 
          id="testimonials-header"
          ref={el => elementsRef.current.header = el}
          isVisible={visibleElements["testimonials-header"]}
        >
          <SectionTitle>Client <HighlightText>Testimonials</HighlightText></SectionTitle>
          <SectionDescription>
            Hear what our clients have to say about our services
          </SectionDescription>
        </SectionHeader>
        
        <TestimonialsContainer 
          id="testimonials-carousel"
          ref={el => elementsRef.current.carousel = el}
          isVisible={visibleElements["testimonials-carousel"]}
        >
          <TestimonialCarousel 
            onMouseEnter={pauseCarousel}
            onMouseLeave={resumeCarousel}
          >
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={testimonial.id}
                isActive={index === activeTestimonial}
                isNext={(index === (activeTestimonial + 1) % testimonials.length)}
                isPrev={(index === (activeTestimonial - 1 + testimonials.length) % testimonials.length)}
              >
                
                
                <QuoteIcon>
                  <FaQuoteRight />
                </QuoteIcon>
                
                <TestimonialQuote>
                  "{testimonial.quote}"
                </TestimonialQuote>
                
                <TestimonialRating>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i}><FaStar /></Star>
                  ))}
                </TestimonialRating>
                
                <TestimonialAuthor>
                  <AuthorImage src={testimonial.image} alt={testimonial.name} />
                  <AuthorDetails>
                    <AuthorName>{testimonial.name}</AuthorName>
                    <AuthorTitle>{testimonial.title}, {testimonial.company}</AuthorTitle>
                  </AuthorDetails>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
            
            <ProgressDisplay>
              <CurrentSlide>Slide {activeTestimonial + 1} of {testimonials.length}</CurrentSlide>
              <ProgressTrack>
                <ProgressFill 
                  width={progress} 
                  isPaused={isPaused}
                />
              </ProgressTrack>
            </ProgressDisplay>
            
            <CarouselControls>
              <ControlsRow>
                <ControlButton onClick={prevTestimonial} aria-label="Previous testimonial">
                  <FaArrowLeft />
                </ControlButton>
                
                <CarouselDots>
                  {testimonials.map((_, index) => (
                    <CarouselDot 
                      key={index}
                      isActive={index === activeTestimonial}
                      onClick={() => {
                        setIsPaused(true);
                        setActiveTestimonial(index);
                        setProgress(0);
                      }}
                    >
                      <DotLabel>Slide {index + 1}</DotLabel>
                    </CarouselDot>
                  ))}
                </CarouselDots>
                
                <ControlButton onClick={nextTestimonial} aria-label="Next testimonial">
                  <FaArrowRight />
                </ControlButton>
                
                <PauseButton 
                  onClick={togglePause} 
                  aria-label={isPaused ? "Play" : "Pause"}
                  isPaused={isPaused}
                >
                  {isPaused ? <FaPlay /> : <FaPause />}
                  <ButtonTooltip>{isPaused ? "Resume autoplay" : "Pause autoplay"}</ButtonTooltip>
                </PauseButton>
              </ControlsRow>
            </CarouselControls>
          </TestimonialCarousel>
        </TestimonialsContainer>
      </Container>
    </TestimonialsSection>
  );
};

export default Testimonials;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const floatAnimation = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(0, -8px) rotate(2deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// === Styled Components ===
const TestimonialsSection = styled.section`
  padding: 100px 40px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%);
  min-height: 70vh;
  display: flex;
  justify-content: center;
  
  @media (max-width: 992px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 60px 20px;
  }
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const GeometricShape = styled.div`
  position: absolute;
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  width: ${props => props.size};
  height: ${props => props.size};
  background-color: ${props => props.color};
  opacity: ${props => props.isVisible ? 0.05 : 0};
  z-index: 0;
  transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.8)'};
  transition: opacity 0.3s ease, transform 0.3s ease;
  animation: ${props => props.isVisible ? floatAnimation : 'none'} 6s infinite ease-in-out;
  
  &.circle {
    border-radius: 50%;
  }
  
  &.square {
    transform: ${props => props.isVisible ? 'rotate(45deg)' : 'rotate(0deg) scale(0.8)'};
    transition: opacity 0.3s ease, transform 0.3s ease;
  }
  
  &.triangle {
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
  
  &.hexagon {
    clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  width: 100%;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  z-index: 2;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Testimonials styling
const TestimonialsContainer = styled.div`
  width: 100%;
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.3s ease, transform 0.3s ease;
`;

const TestimonialCarousel = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  min-height: 480px;
  
  @media (max-width: 768px) {
    min-height: 520px;
  }
  
  @media (max-width: 576px) {
    min-height: 550px;
  }
`;

const HoverNotice = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.05);
  color: #777;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  opacity: 0.7;
  transition: opacity 0.3s;
  
  ${TestimonialCarousel}:hover & {
    opacity: 1;
  }
`;

const TestimonialCard = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  opacity: ${props => props.isActive ? 1 : 0};
  z-index: ${props => props.isActive ? 2 : (props.isNext || props.isPrev ? 1 : 0)};
  transform: ${props => 
    props.isActive ? 'translateX(0) scale(1)' : 
    props.isNext ? 'translateX(5%) scale(0.95)' : 
    props.isPrev ? 'translateX(-5%) scale(0.95)' : 'translateX(0) scale(0.9)'
  };
  transition: transform 0.4s ease, opacity 0.4s ease;
  visibility: ${props => props.isActive || props.isNext || props.isPrev ? 'visible' : 'hidden'};
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 576px) {
    padding: 25px 20px;
  }
`;

const QuoteIcon = styled.div`
  color: #2090B0;
  font-size: 24px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const TestimonialQuote = styled.blockquote`
  font-size: 18px;
  line-height: 1.7;
  color: #444;
  margin-bottom: 20px;
  font-style: italic;
  
  @media (max-width: 576px) {
    font-size: 16px;
  }
`;

const TestimonialRating = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const Star = styled.div`
  color: #FFB400;
  font-size: 18px;
  margin-right: 4px;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 15px;
  border: 2px solid #f0f0f0;
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px;
  color: #105090;
  
  @media (max-width: 576px) {
    font-size: 16px;
  }
`;

const AuthorTitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const ProgressDisplay = styled.div`
  position: absolute;
  margin-top: 20px;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(5px);
  padding: 10px 20px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  
  @media (max-width: 576px) {
    width: 90%;
    bottom: 140px;
  }
`;

const CurrentSlide = styled.div`
  font-size: 14px;
  margin-top:20px;
  color: #105090;
  font-weight: 600;
  margin-bottom: 8px;
`;

const AutoplayStatus = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 6px;
  font-style: italic;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.width}%;
  background: linear-gradient(90deg, #105090, #2090B0);
  border-radius: 3px;
  transition: width ${props => props.isPaused ? '0s' : '0.1s'} linear;
`;

const CarouselControls = styled.div`
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
`;

const ControlButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: #105090;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
    color: #2090B0;
  }
  
  @media (max-width: 576px) {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  gap: 10px;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.isActive ? '#2090B0' : '#E0E0E0'};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    background: ${props => props.isActive ? '#2090B0' : '#BBBBBB'};
    transform: scale(1.2);
    
    div {
      opacity: 1;
      visibility: visible;
    }
  }
  
  ${props => props.isActive && css`
    animation: ${pulse} 2s infinite ease-in-out;
  `}
`;

const DotLabel = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  background: rgba(0,0,0,0.7);
  color: white;
  font-size: 11px;
  padding: 3px 6px;
  border-radius: 4px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  pointer-events: none;
`;

const PauseButton = styled(ControlButton)`
  margin-left: 5px;
  background: ${props => props.isPaused ? 
    'linear-gradient(135deg, #105090, #105090)' : 
    'linear-gradient(135deg, #2090B0, #2090B0)'};
  color: white;
  
  &:hover {
    background: ${props => props.isPaused ? '#105090' : '#2090B0'};
    color: white;
  }
`;

const ButtonTooltip = styled.span`
  position: absolute;
  bottom: 45px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  background: rgba(0,0,0,0.7);
  color: white;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 4px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
  pointer-events: none;
  
  ${ControlButton}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;